<script setup lang="ts">
/**
 * An indeterminate activity ring. It sizes like an icon (`size` = the
 * `--nxt-icon-size-*` scale) and draws in `currentColor`, so it drops in where
 * an icon sat — a refresh button, a list row, a button's leading icon.
 *
 * With `label` it is a status (`role="status"`, the label as visually hidden
 * text) that screen readers announce; without, it is decoration and the
 * surrounding control carries the busy state (`aria-busy`). Under
 * `prefers-reduced-motion` the ring pulses instead of turning.
 */
export type NxSpinnerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const props = withDefaults(
  defineProps<{
    /** Icon size step. Defaults to `md`. */
    size?: NxSpinnerSize;
    /** Announced text („Wird geladen …“); without it the spinner is decorative. */
    label?: string;
  }>(),
  { size: 'md', label: '' }
);
</script>

<template>
  <span
    class="nx-spinner"
    :class="`size-${props.size}`"
    :role="props.label ? 'status' : undefined"
    :aria-hidden="props.label ? undefined : 'true'"
    data-testid="nx-spinner"
  >
    <svg class="nx-spinner__ring" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle class="nx-spinner__track" cx="12" cy="12" r="9" />
      <circle class="nx-spinner__arc" cx="12" cy="12" r="9" pathLength="100" />
    </svg>
    <span v-if="props.label" class="nx-spinner__label">{{ props.label }}</span>
  </span>
</template>

<style scoped>
.nx-spinner {
  --spinner-size: var(--nxt-icon-size-md);

  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: var(--spinner-size);
  height: var(--spinner-size);
  color: inherit;
  vertical-align: middle;
}
.nx-spinner.size-xs {
  --spinner-size: var(--nxt-icon-size-xs);
}
.nx-spinner.size-sm {
  --spinner-size: var(--nxt-icon-size-sm);
}
.nx-spinner.size-lg {
  --spinner-size: var(--nxt-icon-size-lg);
}
.nx-spinner.size-xl {
  --spinner-size: var(--nxt-icon-size-xl);
}
.nx-spinner__ring {
  width: 100%;
  height: 100%;
  animation: nx-spinner-turn calc(var(--nxt-motion-slow) * 3) linear infinite;
}
.nx-spinner__track,
.nx-spinner__arc {
  stroke: currentColor;
  stroke-width: 2.5;
}
.nx-spinner__track {
  opacity: 0.2;
}
.nx-spinner__arc {
  stroke-dasharray: 28 100;
  stroke-linecap: round;
}
.nx-spinner__label {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  margin: calc(var(--nxt-hairline) * -1);
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
@keyframes nx-spinner-turn {
  to {
    transform: rotate(360deg);
  }
}
@keyframes nx-spinner-pulse {
  50% {
    opacity: 0.4;
  }
}
@media (prefers-reduced-motion: reduce) {
  .nx-spinner__ring {
    animation: nx-spinner-pulse calc(var(--nxt-motion-slow) * 6) ease-in-out infinite;
  }
}
</style>
